<template>
  <div class="page-container" :style="rootStyle">
    <div v-if="hasBg" class="page-container__bg-center" :style="centerBgStyle"></div>
    <div class="page-container__body">
      <slot></slot>
    </div>
    <div class="page-container__safe-bottom" :style="safeBottomStyle"></div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useNavigationHeight } from './useNavigationHeight';
import { pxToVw } from '@utils/tool';

const props = withDefaults(
  defineProps<{
    /** 上中下拼接背景图，传 1~3 项分别对应 top/center/bottom */
    bgImgList?: { url: string; height: number }[];
  }>(),
  {
    bgImgList: () => [],
  },
);

const navigationHeight = useNavigationHeight();

const safeBottomStyle = computed(() => ({
  height: `${navigationHeight.value}px`,
}));

const hasBg = computed(() => props.bgImgList.length > 0);

const rootStyle = computed(() => {
  if (!hasBg.value) return undefined;

  const topHeight = props.bgImgList[0]?.height ?? 0;
  const bottomHeight = props.bgImgList[2]?.height ?? 0;
  const totalHeight =
    props.bgImgList.length === 3
      ? props.bgImgList[0].height + props.bgImgList[1].height + props.bgImgList[2].height
      : 0;

  return {
    backgroundImage: `url(${props.bgImgList[0]?.url ?? ''}), url(${props.bgImgList[2]?.url ?? ''})`,
    backgroundSize: `100% ${pxToVw(topHeight)}, 100% ${pxToVw(bottomHeight)}`,
    backgroundRepeat: 'no-repeat, no-repeat',
    backgroundPosition: 'top center, bottom center',
    minHeight: totalHeight > 0 ? pxToVw(totalHeight) : undefined,
  };
});

const centerBgStyle = computed(() => {
  if (props.bgImgList.length < 2) return undefined;

  const centerHeight = props.bgImgList[1].height + 4;
  const topHeight = (props.bgImgList[0]?.height ?? 0) - 2;
  const bottomHeight = (props.bgImgList[2]?.height ?? 0) - 2;

  return {
    backgroundImage: `url(${props.bgImgList[1].url})`,
    backgroundSize: `100% ${pxToVw(centerHeight)}`,
    top: pxToVw(topHeight),
    bottom: pxToVw(bottomHeight),
  };
});
</script>

<style lang="less" scoped>
.page-container {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;

  &__bg-center {
    position: absolute;
    width: 100%;
    left: 0;
    right: 0;
    background-position: center;
    background-size: 100% 1px;
    z-index: 1;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
  }

  &__safe-bottom {
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    width: 100%;
  }
}
</style>
